import styled, { keyframes } from "styled-components";

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 300;
  display: flex;
  justify-content: flex-end;
`;

export const Panel = styled.div`
  width: 560px;
  height: 100%;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.22s ease;
  box-shadow: -4px 0 32px rgba(0, 0, 0, 0.12);
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

export const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 800;
  color: #111;
  letter-spacing: -0.02em;
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background-color: #f5f5f5;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  transition: background-color 0.15s;

  &:hover { background-color: #ebebeb; }
`;

export const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/* ── 이미지 뷰어 ── */

export const ImageViewer = styled.div`
  width: 100%;
  height: 240px;
  border-radius: 14px;
  overflow: hidden;
  background-color: #f0f0f0;
  flex-shrink: 0;
`;

export const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: #ccc;
`;

export const ImageThumbs = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ThumbItem = styled.img<{ isActive?: boolean }>`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  border: 2.5px solid ${({ isActive }) => (isActive ? "#1fbc70" : "transparent")};
  opacity: ${({ isActive }) => (isActive ? 1 : 0.65)};
  transition: opacity 0.15s, border-color 0.15s;

  &:hover { opacity: 1; }
`;

/* ── 섹션 ── */

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SectionTitle = styled.p`
  font-size: 12px;
  font-weight: 700;
  color: #888;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const AssignButton = styled.button`
  height: 30px;
  padding: 0 12px;
  background-color: #1fbc70;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover { background-color: #18a860; }
`;

/* ── 기본 정보 그리드 ── */

export const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
`;

export const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr;
  border-bottom: 1px solid #f5f5f5;

  &:last-child { border-bottom: none; }
`;

export const InfoLabel = styled.span`
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #888;
  background-color: #fafafa;
`;

export const InfoValue = styled.span`
  padding: 12px 16px;
  font-size: 13px;
  color: #111;
  display: flex;
  align-items: center;
`;

export const LabelBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  background-color: #f0fdf7;
  border: 1px solid #d0f0e0;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  color: #1fbc70;
`;

export const CoordText = styled.span`
  font-family: monospace;
  font-size: 13px;
  color: #555;
`;

/* ── 운영 시간 ── */

export const OpTimeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
`;

export const OpTimeHead = styled.thead`
  background-color: #fafafa;
`;

export const OpTh = styled.th`
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-align: left;
  letter-spacing: 0.03em;
`;

export const OpTr = styled.tr`
  border-top: 1px solid #f5f5f5;
`;

export const OpTd = styled.td`
  padding: 10px 16px;
  font-size: 13px;
  color: #333;
`;

/* ── 시설 목록 ── */

export const FacilityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FacilityCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  background-color: #fff;
  transition: background-color 0.12s;

  &:hover { background-color: #fafff9; }
`;

export const FacilityThumb = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
  background-color: #f0f0f0;
`;

export const FacilityThumbPlaceholder = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

export const FacilityInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FacilityName = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FacilityMeta = styled.p`
  font-size: 12px;
  color: #aaa;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FacilityEmpty = styled.p`
  text-align: center;
  font-size: 13px;
  color: #bbb;
  padding: 28px 0;
  border: 1px dashed #eee;
  border-radius: 10px;
`;

/* ── 헤더 액션 ── */

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const EditButton = styled.button`
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background-color: #1fbc70;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover { background-color: #18a360; }
`;

/* ── 수정 폼 ── */

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

export const FormLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #666;
`;

export const FormInput = styled.input`
  height: 36px;
  padding: 0 12px;
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  font-size: 13px;
  color: #111;
  background-color: #fff;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.15s;

  &::placeholder { color: #bbb; }
  &:focus { outline: none; border-color: #1fbc70; }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; }
`;

export const ArrayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: 1px solid #f0d0d0;
  border-radius: 6px;
  background-color: #fff5f5;
  color: #fc324a;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s;

  &:hover { background-color: #fde8e8; }
`;

export const AddButton = styled.button`
  height: 34px;
  padding: 0 12px;
  border: 1.5px dashed #d0d0d0;
  border-radius: 8px;
  background-color: transparent;
  color: #888;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover { border-color: #1fbc70; color: #1fbc70; }
`;

/* ── 이미지 업로드 ── */

export const ThumbUploadWrap = styled.div<{ hasImage: boolean }>`
  position: relative;
  width: 100%;
  height: 160px;
  border-radius: 12px;
  overflow: hidden;
  background-color: #f5f5f5;
  cursor: pointer;
  border: 2px dashed ${({ hasImage }) => (hasImage ? "transparent" : "#d0d0d0")};
  transition: border-color 0.15s;

  &:hover { border-color: #1fbc70; }
`;

export const ThumbUploadImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const ThumbUploadPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #bbb;
  font-size: 13px;
`;

export const ThumbUploadOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.15s;

  ${ThumbUploadWrap}:hover & { opacity: 1; }
`;

export const ImgGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ImgGridItem = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f0f0f0;
  flex-shrink: 0;
`;

export const ImgGridImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const ImgGridRemove = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;

  ${ImgGridItem}:hover & { opacity: 1; }
`;

export const ImgGridLoading = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #aaa;
  flex-shrink: 0;
`;

export const ImgAddBtn = styled.button`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  border: 1.5px dashed #d0d0d0;
  background-color: transparent;
  color: #aaa;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s, color 0.15s;

  &:hover { border-color: #1fbc70; color: #1fbc70; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* ── 저장 / 취소 ── */

export const FormActions = styled.div`
  display: flex;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
`;

export const SaveButton = styled.button`
  flex: 1;
  height: 42px;
  border: none;
  border-radius: 10px;
  background-color: #1fbc70;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover:not(:disabled) { background-color: #18a360; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const CancelButton = styled.button`
  flex: 1;
  height: 42px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background-color: #fff;
  color: #555;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover:not(:disabled) { background-color: #f5f5f5; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const SubmitError = styled.p`
  font-size: 13px;
  color: #fc324a;
  text-align: center;
`;

/* ── 로딩 ── */

export const LoadingWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LoadingText = styled.p`
  font-size: 14px;
  color: #bbb;
`;
